import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Building2, User } from 'lucide-react';

export default function Signup() {
  const [userType, setUserType] = useState(null); // 'organization' or 'candidate'
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match!');
      return;
    }
    
    // Validate company field for organizations
    if (userType === 'organization' && !formData.company.trim()) {
      toast.error('Company name is required for organizations');
      return;
    }
    
    setLoading(true);
    try {
      await api.signup({
        email: formData.email,
        password: formData.password,
        full_name: formData.fullName,
        company: formData.company,
        user_type: userType
      });
      await refreshUser();
      toast.success('Account created successfully!');
      navigate('/');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  if (!userType) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-4xl">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <div className="bg-blue-600 text-white font-bold px-6 py-3 text-3xl rounded-lg shadow-lg hover:bg-blue-700 transition-all">
                Symetrix
              </div>
            </Link>
          </div>
          
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-3">Get Started</h1>
            <p className="text-lg text-gray-600">Choose your account type</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Organization Signup */}
            <Card 
              className="border-2 border-transparent hover:border-blue-500 hover:shadow-2xl transition-all cursor-pointer group"
              onClick={() => setUserType('organization')}
              data-testid="organization-signup-card"
            >
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <Building2 className="text-white" size={40} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">I'm an Organization</h2>
                <p className="text-gray-600 mb-6">
                  Post jobs, review applications, and hire the best talent for your company
                </p>
                <Button className="bg-blue-600 hover:bg-blue-700 w-full" size="lg">
                  Sign up as Organization
                </Button>
              </CardContent>
            </Card>

            {/* Candidate Signup */}
            <Card 
              className="border-2 border-transparent hover:border-green-500 hover:shadow-2xl transition-all cursor-pointer group"
              onClick={() => setUserType('candidate')}
              data-testid="candidate-signup-card"
            >
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <User className="text-white" size={40} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">I'm a Candidate</h2>
                <p className="text-gray-600 mb-6">
                  Browse jobs, apply for positions, and take the next step in your career
                </p>
                <Button className="bg-green-600 hover:bg-green-700 w-full" size="lg">
                  Sign up as Candidate
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-semibold">
                Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <div className="bg-blue-600 text-white font-bold px-4 py-2 text-2xl rounded-lg">
              Symetrix
            </div>
          </Link>
        </div>
        
        <Card className="border-none shadow-xl">
          <CardContent className="p-8">
            {/* Back Button */}
            <button
              onClick={() => setUserType(null)}
              className="text-sm text-gray-600 hover:text-gray-900 mb-6 flex items-center gap-2"
            >
              ← Back to selection
            </button>

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                {userType === 'organization' ? (
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Building2 className="text-blue-600" size={24} />
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <User className="text-green-600" size={24} />
                  </div>
                )}
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    {userType === 'organization' ? 'Organization Signup' : 'Candidate Signup'}
                  </h1>
                </div>
              </div>
              <p className="text-gray-600">
                {userType === 'organization' 
                  ? 'Create an account to start hiring top talent'
                  : 'Create an account to start your job search'
                }
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <Input
                  name="fullName"
                  type="text"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                  className="bg-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                  className="bg-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company {userType === 'organization' && <span className="text-red-500">*</span>}
                  {userType === 'candidate' && <span className="text-gray-500 text-xs">(Optional)</span>}
                </label>
                <Input
                  name="company"
                  type="text"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder={userType === 'organization' ? 'Enter your company name' : 'Current or previous company'}
                  required={userType === 'organization'}
                  className="bg-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <Input
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password"
                  required
                  className="bg-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <Input
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  required
                  className="bg-white"
                />
              </div>
              
              <Button 
                type="submit" 
                className={`w-full ${
                  userType === 'organization' 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-green-600 hover:bg-green-700'
                }`}
                disabled={loading}
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>
            
            <div className="mt-6 text-center text-sm text-gray-600">
              <p>
                Already have an account?{' '}
                <Link to="/login" className="text-blue-600 hover:text-blue-700 font-semibold">
                  Login
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
